<script setup lang="ts">
/*
 * Account entry in the header: login/sign-up when signed out, otherwise an
 * avatar dropdown with the account destinations, the language switch and
 * sign out. Destinations come from accountLinks.ts (shared with the drawer).
 */
import { computed, watch } from 'vue';
import { RouterLink, useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { ChevronDown, LogOut, User } from 'lucide-vue-next';
import LanguageMenu from './LanguageMenu.vue';
import { ACCOUNT_LINKS, ADMIN_LINK } from './accountLinks';
import { useDropdown } from './useDropdown';
import { useAuthStore } from '../../stores/auth';

const { t } = useI18n();
const auth = useAuthStore();
const router = useRouter();
const route = useRoute();
const { root, open, close, toggle, onFocusOut } = useDropdown();

const displayName = computed(
  () =>
    [auth.user?.firstName, auth.user?.lastName].filter(Boolean).join(' ') ||
    auth.user?.username ||
    auth.user?.email?.split('@')[0] ||
    t('header.accountFallback'),
);

// Two letters of whatever identifies the user best; never empty.
const initials = computed(() => {
  const { firstName, lastName, username, email } = auth.user ?? {};
  const names = [firstName, lastName].map((n) => n?.trim()[0]).filter(Boolean);
  if (names.length) return names.join('').toUpperCase();
  return (username?.trim() || email || 'U').slice(0, 2).toUpperCase();
});

const links = computed(() => (auth.isStaff ? [...ACCOUNT_LINKS, ADMIN_LINK] : ACCOUNT_LINKS));

watch(() => route.fullPath, close);

async function logout() {
  close();
  await auth.logout();
  void router.push('/');
}
</script>

<template>
  <div v-if="auth.isAuthenticated" ref="root" class="relative" @focusout="onFocusOut">
    <button
      type="button"
      data-dropdown-trigger
      aria-haspopup="menu"
      :aria-expanded="open"
      :aria-label="t('header.aria.userMenu', { name: displayName })"
      class="flex h-11 items-center gap-2 rounded-full border border-border bg-bg-elev-2 pl-1 pr-1 transition-colors hover:border-white lg:pr-3"
      @click="toggle"
    >
      <span class="grid size-9 place-items-center rounded-full bg-accent font-heading text-[11px] font-bold text-text-inv">{{ initials }}</span>
      <span class="hidden max-w-[120px] truncate text-[13px] font-semibold text-text-base lg:block">{{ displayName }}</span>
      <ChevronDown class="hidden h-3.5 w-3.5 text-text-faint lg:block" aria-hidden="true" />
    </button>

    <div v-if="open" role="menu" class="surface animate-pop absolute right-0 top-[calc(100%+10px)] z-40 w-[260px] p-1.5">
      <div class="px-3 pb-3 pt-2">
        <p class="truncate text-[14px] font-semibold text-text-base">{{ displayName }}</p>
        <p class="truncate text-[12px] text-text-dim">{{ auth.user?.email }}</p>
      </div>
      <div aria-hidden="true" class="mx-1 h-px bg-border"></div>
      <ul class="flex flex-col py-1">
        <li v-for="l in links" :key="l.to">
          <RouterLink
            :to="l.to"
            role="menuitem"
            class="flex h-9 items-center gap-3 rounded-md px-3 text-[14px] transition-colors hover:bg-bg-elev-2"
            :class="l === ADMIN_LINK ? 'text-accent' : 'text-text-base'"
          >
            <component :is="l.icon" class="h-4 w-4" aria-hidden="true" />
            {{ t(`header.menu.${l.label}`) }}
          </RouterLink>
        </li>
      </ul>
      <div aria-hidden="true" class="mx-1 h-px bg-border"></div>
      <div class="flex items-center justify-between px-3 py-2">
        <span class="text-[13px] text-text-dim">{{ t('header.menu.language') }}</span>
        <LanguageMenu />
      </div>
      <div aria-hidden="true" class="mx-1 h-px bg-border"></div>
      <button
        type="button"
        role="menuitem"
        class="mt-1 flex h-9 w-full items-center gap-3 rounded-md px-3 text-[14px] text-error transition-colors hover:bg-error/10"
        @click="logout"
      >
        <LogOut class="h-4 w-4" aria-hidden="true" />
        {{ t('common.actions.signOut') }}
      </button>
    </div>
  </div>

  <div v-else class="flex items-center gap-1">
    <RouterLink to="/login" class="btn-ghost btn-sm hidden !h-10 sm:inline-flex">{{ t('header.login') }}</RouterLink>
    <RouterLink to="/register" class="btn-secondary btn-sm hidden !h-10 sm:inline-flex">{{ t('header.signup') }}</RouterLink>
    <RouterLink to="/login" :aria-label="t('header.login')" class="btn-icon sm:hidden">
      <User class="h-5 w-5" aria-hidden="true" />
    </RouterLink>
  </div>
</template>
